import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { DayritLogo, LogoText } from './ui';
import { PomodoroModal } from './PomodoroTimer';

function HamburgerIcon({ open }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      {open
        ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
        : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
      }
    </svg>
  );
}

export default function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close drawer on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Desktop sidebar — hidden below 768px ── */}
      <div style={{
        display: 'none',
      }} className="sidebar-desktop">
        <style>{`
          @media (min-width: 768px) {
            .sidebar-desktop { display: block !important; }
            .mobile-topbar   { display: none !important; }
          }
          @media (max-width: 767px) {
            .sidebar-desktop { display: none !important; }
            .mobile-topbar   { display: flex !important; }
          }
        `}</style>
        <Sidebar />
      </div>

      {/* ── Mobile top bar ── */}
      <div className="mobile-topbar" style={{
        display: 'none', // overridden by media query
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        height: 52, background: 'var(--sidebar-bg)',
        borderBottom: '1px solid var(--border)',
        alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
      }}>
        <button
          onClick={() => setDrawerOpen(p => !p)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text2)', padding: '6px',
            borderRadius: 7, display: 'flex', alignItems: 'center',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <HamburgerIcon open={drawerOpen} />
        </button>

        {/* Logo centred — same components as sidebar */}
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <DayritLogo size={24} />
          <LogoText size={15} />
        </div>

        {/* Spacer */}
        <div style={{ width: 32 }} />
      </div>

      {/* ── Mobile drawer overlay ── */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 150,
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* ── Mobile drawer — only mounted when open to prevent shadow bleed ── */}
      {drawerOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 160,
          width: 240,
          boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
          animation: 'drawerSlideIn 0.22s ease',
        }}>
          <Sidebar onClose={() => setDrawerOpen(false)} hideHeader />
        </div>
      )}
      <style>{`@keyframes drawerSlideIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}`}</style>

      {/* ── Pomodoro modal — lives here, independent of sidebar ── */}
      <PomodoroModal />

      {/* ── Main content ── */}
      <main style={{
        flex: 1,
        minWidth: 0,
        // On mobile: top padding for the fixed top bar
        paddingTop: 0,
      }}>
        <style>{`
          @media (max-width: 767px) {
            .app-main-content { padding-top: 52px !important; }
          }
        `}</style>
        <div className="app-main-content" style={{ paddingTop: 0 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}